/**
 * Shipping Protection — Cart Toggle
 * File: assets/shipping-protection.js
 *
 * How to load:
 *   Add to your theme.liquid or cart-drawer.liquid:
 *   <script src="{{ 'shipping-protection.js' | asset_url }}" defer></script>
 */

const shippingProtection = (() => {

  // ─── Internal helpers ─────────────────────────────────────────────────────

  function getBlock() {
    return document.getElementById('shipping-protection-block');
  }

  function getVariantId() {
    const block = getBlock();
    return block ? block.dataset.spVariant : null;
  }

  function setLoading(isLoading) {
    const block = getBlock();
    if (!block) return;
    block.classList.toggle('sp-loading', isLoading);
    const input = document.getElementById('sp-toggle-input');
    if (input) input.disabled = isLoading;
  }

  function setStatus(isOn) {
    const status = document.getElementById('sp-status');
    if (!status) return;

    const checkIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;
    const crossIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

    if (isOn) {
      status.innerHTML = `<span class="sp-badge sp-badge--on">${checkIcon} Protection active</span>`;
    } else {
      status.innerHTML = `<span class="sp-badge sp-badge--off">${crossIcon} Not protected</span>`;
    }
  }

  // ─── Add product to cart ──────────────────────────────────────────────────

  async function addToCart(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }]
      })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to add protection');
    }

    return response.json();
  }

  // ─── Remove product from cart ─────────────────────────────────────────────

  async function removeFromCart(variantId) {
    // Get current line key for this variant
    const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await cartRes.json();

    const line = cart.items.find(i => String(i.variant_id) === String(variantId));
    if (!line) return; // already not in cart

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to remove protection');
    }

    return response.json();
  }

  // ─── Refresh cart drawer ──────────────────────────────────────────────────
  // Dispatches the standard Shopify cart:refresh event and also calls common
  // theme refresh methods used by Dawn, Impulse, Prestige, Turbo, etc.

    function refreshCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    }

  // ─── Public toggle handler (called by the checkbox onchange) ─────────────

async function toggle(checkbox) {
  const variantId = getVariantId();
  if (!variantId) { checkbox.checked = !checkbox.checked; return; }

  setLoading(true);

  try {
    if (checkbox.checked) {
      await addToCart(variantId);
      setStatus(true);
    } else {
      await removeFromCart(variantId);
      setStatus(false);
    }

    // Impulse: rebuild the cart drawer UI
    document.dispatchEvent(new CustomEvent('cart:build'));

  } catch (err) {
    console.error('[ShippingProtection]', err.message);
    checkbox.checked = !checkbox.checked;
    setStatus(checkbox.checked);
  } finally {
    setLoading(false);
  }
}

  // ─── Auto-init: ensure protection is added on page load if toggled on ─────
  // Useful when the cart drawer is re-rendered by the theme.

function init() {
    const block = getBlock();
    if (!block) return;

    const variantId = getVariantId();
    const inCart = block.dataset.spInCart === 'true';
    const input  = document.getElementById('sp-toggle-input');

    if (!input) return;

    // Force input to be checked on initialization
    input.checked = true;

    // If it's not in the cart yet, add it automatically
    if (!inCart && variantId) {
      setLoading(true);
      addToCart(variantId)
        .then(() => { 
          setStatus(true); 
          // Update the dataset so we don't accidentally trigger it again
          block.dataset.spInCart = 'true'; 
          refreshCartDrawer(); 
        })
        .catch(err => console.warn('[ShippingProtection] Auto-add failed:', err.message))
        .finally(() => setLoading(false));
    } else if (inCart) {
      setStatus(true);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if cart drawer HTML is re-rendered by the theme
  document.addEventListener('cart:refresh', () => setTimeout(init, 300));

  // ─── Public API ───────────────────────────────────────────────────────────
  return { toggle, addToCart, removeFromCart, refreshCartDrawer, init };

})();